import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Feedback, FeedbackStatus, FeedbackType } from '@brafton/feedback-api';
import {
  CommentThread,
  type CommentThreadEntry,
  type NewComment,
  StatusChip,
} from '@brafton/feedback-components';

import { FeedbackStatusChange } from '../../components/feedback-list/feedback-list';

/** The marker's viewport rectangle the popover positions itself against. */
export interface AnchorRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The presentational view-model derived from a Feedback item. */
interface DetailView {
  number: number;
  category: string;
  element: string;
  observation: string;
  recommendation: string;
  reporterName: string;
  reporterEmail: string;
  initials: string;
  status: string;
  device: string;
  deviceLabel: string;
  source: 'User' | 'AI';
  reported: string;
  os: string;
  browser: string;
  screenshotUrl: string | null;
  assets: { name: string; url: string }[];
}

/** UX/SEO read better fully capitalised; everything else is title-cased. */
const CATEGORY_OVERRIDES: Record<string, string> = { ux: 'UX', seo: 'SEO' };

/**
 * Feedback-detail popover, positioned next to its marker. Populated + styled per
 * the design: header (number, category, element), reporter + status chip,
 * observation, screenshot, and a meta grid (Device, Source, Reported, OS,
 * Browser). "Source" is the feedback type; "Browser" combines the browser name
 * and version, and the discussion thread comes from the comments API.
 *
 * Only PUBLIC comments ever reach here: the widget calls with a round-scoped token,
 * which the API filters private comments out of. The composer likewise cannot mark a
 * comment private — fb-comment-thread's canPostPrivate is left at its default.
 */
@Component({
  selector: 'app-feedback-detail',
  templateUrl: './feedback-detail.html',
  styleUrls: ['./feedback-detail.scss', './feedback-detail.premium.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [StatusChip, CommentThread],
})
export class FeedbackDetail {
  /** Whether the popover is shown. */
  readonly open = input(false);

  /** The marker rectangle (viewport coords) to position beside. */
  readonly anchor = input<AnchorRect | null>(null);

  /** The feedback being shown. */
  readonly feedback = input<Feedback | null>(null);

  /** The feedback app origin, for the "open in app" link. */
  readonly appBaseUrl = input('');

  /** The feedback round id, for the "open in app" link target. */
  readonly feedbackRoundId = input('');

  /**
   * Whether the reporter is logged into the main app (via the auth bridge). The
   * "open in app" link only shows when true.
   */
  readonly loggedIn = input(false);

  /** Emits when the popover should close. */
  readonly close = output<void>();

  /** Emits when the status is changed via the chip. */
  readonly statusChange = output<FeedbackStatusChange>();

  /** The public comments on this feedback item. */
  readonly comments = input<CommentThreadEntry[]>([]);

  /** True while the thread is loading. */
  readonly commentsLoading = input(false);

  /** True while a comment is being sent. */
  readonly commentPosting = input(false);

  /** Emitted when the reporter sends a comment; the host performs the write. */
  readonly commentPost = output<NewComment>();

  /** Statuses offered in the status chip. */
  protected readonly statuses = Object.values(FeedbackStatus);

  protected readonly left = signal(0);
  protected readonly top = signal(0);

  /**
   * Where the popover was laid out relative to its marker, for the current feedback
   * and viewport size. While those stay the same (i.e. the page is only scrolling),
   * the popover keeps this offset from the marker instead of being laid out again, so
   * it travels with the marker rather than sticking to the viewport edge.
   */
  private placement: {
    feedbackId: string | undefined;
    viewportWidth: number;
    viewportHeight: number;
    offsetLeft: number;
    offsetTop: number;
  } | null = null;

  private readonly popoverEl = viewChild<ElementRef<HTMLElement>>('popoverEl');

  /** The view-model for the current feedback (null when none). */
  protected readonly view = computed<DetailView | null>(() => {
    const item = this.feedback();
    if (!item) {
      return null;
    }
    const meta = (item.meta ?? {}) as Record<string, unknown>;
    const marker = (item.marker ?? {}) as Record<string, unknown>;
    const metaString = (key: string): string =>
      typeof meta[key] === 'string' ? (meta[key] as string) : '';
    const number = Number(marker['id']) || 0;
    const user = this.toUser(item.createdBy);
    const device = metaString('screen') || 'desktop';
    const browser = [metaString('browser'), metaString('appVersion')].filter(Boolean).join(' ');
    return {
      number,
      category: item.category ? this.humanize(item.category) : '',
      element: item.element || `Untitled element #${number}`,
      observation: item.observation ?? '',
      recommendation: item.recommendation ?? '',
      reporterName: user.name,
      reporterEmail: user.email,
      initials: this.initials(user.name),
      status: item.status,
      device,
      deviceLabel: device.charAt(0).toUpperCase() + device.slice(1),
      source: item.type === FeedbackType.AiFeedback ? 'AI' : 'User',
      reported: this.formatDateTime(item.createdAt),
      os: metaString('os') || '—',
      browser: browser || '—',
      screenshotUrl: item.screenshotUrl ?? null,
      assets: (item.fileUrls ?? []).map((url) => ({ name: this.fileName(url), url })),
    };
  });

  /**
   * The open-in-app link: the feedback round in the main app with the feedback
   * deep-link query param, so the item opens there in a new tab.
   */
  protected readonly openInAppUrl = computed(() => {
    const item = this.feedback();
    const base = this.appBaseUrl();
    const roundId = this.feedbackRoundId();
    if (!item || !base || !roundId) {
      return '';
    }
    return `${base.replace(/\/$/, '')}/projects/${roundId}?feedback=${encodeURIComponent(item.id)}`;
  });

  constructor() {
    effect(() => {
      if (!this.open()) {
        this.placement = null;
        return;
      }
      const anchor = this.anchor();
      requestAnimationFrame(() => this.positionByAnchor(anchor));
    });
  }

  protected onStatusChange(status: string): void {
    const item = this.feedback();
    if (item) {
      this.statusChange.emit({ id: item.id, status });
    }
  }

  /** Normalises the free-form `createdBy` into a name/email pair. */
  private toUser(createdBy: unknown): { name: string; email: string } {
    if (typeof createdBy === 'string') {
      return { name: createdBy, email: '' };
    }
    if (createdBy && typeof createdBy === 'object') {
      const record = createdBy as Record<string, unknown>;
      return {
        name: typeof record['name'] === 'string' ? record['name'] : '',
        email: typeof record['email'] === 'string' ? record['email'] : '',
      };
    }
    return { name: '', email: '' };
  }

  /** The trailing file name of a URL, for asset labels. */
  private fileName(url: string): string {
    try {
      const path = new URL(url).pathname;
      return decodeURIComponent(path.split('/').filter(Boolean).pop() ?? url);
    } catch {
      return url.split('/').filter(Boolean).pop() ?? url;
    }
  }

  private initials(name: string): string {
    const trimmed = name.trim();
    if (!trimmed) {
      return '?';
    }
    const parts = trimmed.split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }

  private humanize(value: string): string {
    if (CATEGORY_OVERRIDES[value]) {
      return CATEGORY_OVERRIDES[value];
    }
    return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
  }

  private formatDateTime(iso?: string): string {
    if (!iso) {
      return '—';
    }
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
      return iso;
    }
    return parsed.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  /**
   * Places right of the marker when it fits, else left, else under; then clamps to the
   * viewport. Done when the popover opens (or the viewport is resized); after that, a
   * moved anchor — the page scrolling — just carries the popover along with the marker.
   */
  private positionByAnchor(anchor: AnchorRect | null): void {
    const element = this.popoverEl()?.nativeElement;
    if (!element) {
      return;
    }
    const width = element.offsetWidth;
    const height = element.offsetHeight;
    const gap = 12;
    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (!anchor) {
      this.placement = null;
      this.left.set(Math.round((viewportWidth - width) / 2));
      this.top.set(Math.round((viewportHeight - height) / 2));
      return;
    }

    const feedbackId = this.feedback()?.id;
    const placement = this.placement;
    if (
      placement
      && placement.feedbackId === feedbackId
      && placement.viewportWidth === viewportWidth
      && placement.viewportHeight === viewportHeight
    ) {
      this.left.set(Math.round(anchor.left + placement.offsetLeft));
      this.top.set(Math.round(anchor.top + placement.offsetTop));
      return;
    }

    const spaceRight = viewportWidth - (anchor.left + anchor.width);
    const spaceLeft = anchor.left;

    let left: number;
    let top: number;
    if (spaceRight >= width + gap) {
      left = anchor.left + anchor.width + gap;
      top = anchor.top;
    } else if (spaceLeft >= width + gap) {
      left = anchor.left - gap - width;
      top = anchor.top;
    } else {
      left = anchor.left + anchor.width / 2 - width / 2;
      top = anchor.top + anchor.height + gap;
    }

    left = Math.max(margin, Math.min(left, viewportWidth - width - margin));
    top = Math.max(margin, Math.min(top, viewportHeight - height - margin));
    this.left.set(Math.round(left));
    this.top.set(Math.round(top));
    this.placement = {
      feedbackId,
      viewportWidth,
      viewportHeight,
      offsetLeft: left - anchor.left,
      offsetTop: top - anchor.top,
    };
  }
}
